import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import { isEqual } from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { DashboardContext } from 'live/context/dashboard'

import ParametersList from './ParametersList'

import api from '../api'
import { ParameterKeyProps, ParameterKeyValueProps, ParameterProps, RunParameterProps, RunPathProps } from '../types'
import { useGrantParamsOnURL } from './useGrantParamsOnURL'
import { dispatchParametersUpdatedEvent } from './utils'

export const RUN_PARAMETERS_KEY = 'run-parameters'

export default pluginHeaderNavigationDetected => {
    const [open, setOpen] = useState(false)
    const [runPath, setRunPath] = useState<RunPathProps>()

    const [displayParameters, setDisplayParameters] = useState<ParameterProps[]>()
    const [onResetParameters, setOnResetParameters] = useState<ParameterProps[]>()

    const [defaultRunparameters, setDefaultRunParameters] = useState<ParameterProps[]>()
    const [savedRunparameters, setSavedRunParameters] = useState<RunParameterProps>()
    const [userParameters, setUserParameters] = useState<ParameterKeyValueProps[]>()
    const [dashboardParameters, setDashboardParameters] = useState<ParameterKeyProps[]>()
    const [history, setHistory] = useState<RunParameterProps[]>([])
    const [updateHistory, setUpdateHistory] = useState<boolean>(false)
    const { state, params, ...context } = useContext(DashboardContext)
    const [diffParametersFlag, setDiffParametersFlag] = useState(false)
    const contextParamsRef = useRef(params) // Params received through context, such as span, filter ...
    const displayParametersRef = useRef(displayParameters) // Params
    const updatedFromContext = useRef(false)
    const updatedFromHistory = useRef(false)
    const dashboardId = context?.dashboard?.id
    const mode = params?.mode
    const span = state?.currentSpan

    const toggleParameters = () => setOpen(!open)

    const makeDisplayParameters = (savedRunparameters, dashboardParameters, defaultRunparameters, userParameters): ParameterProps[] => {
        const userParamsMap = userParameters.reduce((map, p) => {
            map[p.key] = p.value
            return map
        }, {})

        return makeOnResetParameters(savedRunparameters, dashboardParameters, defaultRunparameters).map(p => ({ ...p, value: userParamsMap[p.key] ?? p.value }))
    }

    const makeOnResetParameters = (savedRunparameters, dashboardParameters, defaultRunparameters): ParameterProps[] => {
        const savedRunparamsMap = savedRunparameters?.parameters ?? {}

        const dashboardParametersMap: Map<string, boolean> = dashboardParameters.reduce((map, key) => {
            map[key] = true
            return map
        }, {})

        return defaultRunparameters
            .filter(p => (dashboardParametersMap[p.key]))
            .map(p => ({ ...p, value: savedRunparamsMap[p.key].value ?? p.value }))
    }

    let assetLookupIsPresent = true
    if (!Array.isArray(state.currentFilterValues.asset_name_event_type)) {
        assetLookupIsPresent = false
    }

    const operationsSpanPicker = !!context?.dashboard?.jsonConfig?.multipleSpanPicker?.['drilling-operations-span-picker'] || !!context?.dashboard?.jsonConfig?.spanPicker?.type?.['drilling-operations-span-picker']
    const runPathIsValid = !!(runPath?.interventionId && runPath?.drillingSectionPosIndex && runPath?.runPosIndex)

    const asset_name_event_type: string[] = assetLookupIsPresent ? state.currentFilterValues.asset_name_event_type : []
    if (asset_name_event_type.length === 0) {
        console.warn('Run Parameter requires "asset_name_event_type" lookup with single asset configuration.')
        assetLookupIsPresent = false
    } else if (asset_name_event_type.length > 1) {
        console.warn('Run Parameter only work with one asset. Only the first asset of "asset_name_event_type" lookup will be used.')
        assetLookupIsPresent = false
    }

    const wellName = assetLookupIsPresent ? asset_name_event_type[0] : ''

    function fetchAllParameters() {
        setOpen(false)
        const span = encodeURIComponent(state?.currentSpan)

        if (!wellName || !span || !dashboardId || !runPathIsValid) return

        const controller = new AbortController()
        const signal = controller.signal

        let savedRunParametersPromise
        let savedRunParametersPromiseCallback

        let userParametersPromise
        let userParametersPromiseCallback

        let historyRunParametersPromise
        let historyRunParametersPromiseCallback

        if (operationsSpanPicker) {
            savedRunParametersPromise = api.getRunParametersByRunPath(signal, wellName, runPath.interventionId, runPath.drillingSectionPosIndex, runPath.runPosIndex)
            savedRunParametersPromiseCallback = (data) => setSavedRunParameters(data)

            userParametersPromise = api.getUserParametersByRunPath(signal, wellName, runPath.interventionId, runPath.drillingSectionPosIndex, runPath.runPosIndex)
            userParametersPromiseCallback = (data) => setUserParameters(data?.parameters)

            historyRunParametersPromise = api.getRunParametersHistoryByRunPath(signal, wellName, runPath.interventionId, runPath.drillingSectionPosIndex, runPath.runPosIndex)
            historyRunParametersPromiseCallback = (data) => {
                if (!!data) {
                    setHistory(Object.values(data))
                }
            }
        } else {
            savedRunParametersPromise = api.getRunParameters(signal, wellName, span)
            savedRunParametersPromiseCallback = (data) => setSavedRunParameters(data)

            userParametersPromise = api.getUserParameters(signal, wellName, span)
            userParametersPromiseCallback = (data) => setUserParameters(data?.parameters)

            historyRunParametersPromise = api.getRunParametersHistory(signal, wellName, span)
            historyRunParametersPromiseCallback = (data) => {
                if (!!data) {
                    setHistory(Object.values(data))
                }
            }
        }

        const defaultParametersPromise = api.getDefaultParameters(signal)
        const defaultParametersPromiseCallback = (data) => setDefaultRunParameters(data?.parameters)

        const dashboardParametersPromise = api.getDashboardParameters(signal, dashboardId)
        const dashboardParametersPromiseCallback = (data) => setDashboardParameters(data?.parameters)


        const promises = [defaultParametersPromise, dashboardParametersPromise, savedRunParametersPromise, userParametersPromise, historyRunParametersPromise]
        const callbacks = [defaultParametersPromiseCallback, dashboardParametersPromiseCallback, savedRunParametersPromiseCallback, userParametersPromiseCallback, historyRunParametersPromiseCallback];

        Promise.all(promises).then((values) => {
            values.forEach((response, i) => {
                if (response.ok) {
                    response.json().then(data => callbacks[i](data))
                }
            });
        })

        return () => controller.abort()
    }

    function deleteRunParametersHistoryItem(createdAtTimestamp) {
        api.deleteRunParametersHistoryItem(wellName, span, createdAtTimestamp)
            .then(response => {
                if (response.ok) {
                    globalNotification.success("History item deleted successfully.")
                    response.json().then(data => {
                        if (!!data) {
                            setHistory(Object.values(data))
                        }
                    })
                } else {
                    globalNotification.error("Error ocurred while deleting history item.")
                    console.error(response)
                }
            })
    }

    // NOTE:
    // updateDashboardFlag:false is way to control the dashboard updated/reloaded.
    // Ideally there should be another layer that controls it updates/reloads only once if any changes are applied or saved, but it works for now.
    function updateByRun(parameters: ParameterKeyValueProps[], updateDashboardFlag = true) {
        const payload = parameters.map(e => ({ key: e.key, value: e.value }))

        if (operationsSpanPicker) {
            api.saveRunParametersByRunPath(wellName, runPath.interventionId, runPath.drillingSectionPosIndex, runPath.runPosIndex, payload)
                .then(response => {
                    callback(response)
                })
        } else {
            api.saveRunParameters(wellName, encodeURIComponent(state?.currentSpan), payload)
                .then(response => {
                    callback(response)
                })
        }

        toggleParameters()

        function callback(response: Response) {
            if (response.ok) {
                if (updateDashboardFlag) {
                    updateDashboard(parameters, displayParametersRef.current, defaultRunparameters ?? [], context)
                }

                fetchAllParameters()
            } else {
                console.error(response)
            }
        }
    }

    // NOTE:
    // updateDashboardFlag:false is way to control the dashboard updated/reloaded.
    // Ideally there should be another layer that controls it updates/reloads only once if any changes are applied or saved, but it works for now.
    function updateByUser(parameters: ParameterKeyValueProps[], updateDashboardFlag = true) {
        const payload = parameters.map(e => ({ key: e.key, value: e.value }))

        if (operationsSpanPicker) {
            api.saveUserParametersByRunPath(wellName, runPath.interventionId, runPath.drillingSectionPosIndex, runPath.runPosIndex, payload)
                .then(r => {
                    callback(r)
                })
        } else {
            api.saveUserParameters(wellName, encodeURIComponent(state?.currentSpan), payload)
                .then(r => {
                    callback(r)
                })
        }

        toggleParameters()

        function callback(r: Response) {
            if (r.ok) {
                if (updateDashboardFlag) {
                    updateDashboard(parameters, displayParametersRef.current, defaultRunparameters ?? [], context)
                }

                fetchAllParameters()
            } else {
                console.error(r.status, r.statusText)
            }
        }
    }

    function updateDashboard(newParameters: ParameterKeyValueProps[], parametersRef: ParameterProps[], defaultRunparameters: ParameterProps[], context: any) {
        if (!!defaultRunparameters && defaultRunparameters.length === 0)
            return

        const newParametersKeyValueMap = newParameters.reduce((map, p) => {
            map[p.key] = p.value
            return map
        }, {})
        const parametersRefKeyValueMap = parametersRef.reduce((map, p) => {
            map[p.key] = p.value
            return map
        }, {})

        const changedParameters = defaultRunparameters
            .filter((p) => newParametersKeyValueMap[p.key] !== parametersRefKeyValueMap[p.key])
            .map(p => ({ ...p, value: newParametersKeyValueMap[p.key] }))
        const redoQuery = changedParameters.reduce((acc, curr) => acc || curr.redoQuery, false)

        if (redoQuery) {
            context.actions.stop()
            context.actions.start()
        } else {
            dispatchParametersUpdatedEvent(changedParameters)
        }
    }

    function fetchRunPath() {
        const controller = new AbortController()
        const signal = controller.signal

        if (operationsSpanPicker) {
            api.getOperationSpanPickerRunPath(signal, dashboardId, wellName)
                .then((response) => {
                    if (response.ok) {
                        response.json().then(data => {
                            if (data.runPath) {
                                setRunPath(JSON.parse(data.runPath))
                            } else {
                                setRunPath(null)
                                console.warn(`No runPath found on Operations Span Picker configuration for dashboard ${dashboardId}`, data)
                            }
                        })
                    }
                })
                .catch((error) => {
                    console.error(`Error getting Operations Span Picker's runPath for dashboard ${dashboardId}`, error);
                })
        } else {
            api.getRunPath(signal, wellName, span)
                .then((response) => {
                    if (response.ok) {
                        response.json().then(data => {
                            if (data) {
                                setRunPath(data)
                            } else {
                                setRunPath(null)
                                console.warn(`No runPath found for wellName: ${wellName} and end of span: ${span}`)
                            }
                        })
                    }
                })
                .catch((error) => {
                    console.error(`Error getting runPath for dashboard ${dashboardId}`, error);
                })
        }
    }

    /*
    * First useEffect to get URL run-parameters, if any, and update user parameters to reflect on the query
    */
    useEffect(() => {
        if (displayParameters != undefined && displayParameters.length > 0) {
            displayParametersRef.current = displayParameters

            let parametersUpdate = displayParameters

            if (updatedFromContext.current === false && params !== undefined && params[RUN_PARAMETERS_KEY] !== undefined) {  // && !updatedFromHistory.current
                let parametersUpdateArrayOfNames = parametersUpdate.map(e => e.name)

                try {
                    JSON.parse(decodeURIComponent(decodeURIComponent(params[RUN_PARAMETERS_KEY]))).map(e => {
                        if (parametersUpdateArrayOfNames.indexOf(e.name) !== undefined && e.value) {
                            updatedFromContext.current = true
                            parametersUpdate[parametersUpdateArrayOfNames.indexOf(e.name)].value = e.value
                        }
                    })

                    if (updatedFromContext.current) {
                        // NOTE:
                        // updateDashboardFlag:false to do not update dashboard when getting the run-parameters from URL
                        // as it is the first load of the dashboards and all queries are going to run anyway
                        updateByUser(parametersUpdate, false)
                        setOpen(false)
                    }
                } catch (err) {
                    console.error(err)
                }
            }
        }
    }, [JSON.stringify(displayParameters)])

    /*
    * Second useEffect to fetch runPath in case asset_filter, span or dashboard mode changes
    */
    useEffect(() => {
        setOpen(false)

        fetchRunPath()
    }, [wellName, state?.currentSpan, mode])

    /*
    * Second useEffect to fetch all parameters (run, user, dashboard, default) when runPath changes
    */
    useEffect(() => {
        fetchAllParameters()
    }, [runPath])

    /*
    * This useEffect stores the url query search parameters captured by the dashboard in order to compare with any
    * run-parameters changes and keep the URL updated accordingly
    */
    useEffect(() => { contextParamsRef.current = params }, [JSON.stringify(params)])

    useEffect(() => { setDiffParametersFlag(false) }, [wellName, mode, span])

    useEffect(() => { setDiffParametersFlag(!isEqual(onResetParameters, displayParameters)) },
        [JSON.stringify(onResetParameters), JSON.stringify(displayParameters)])

    /*
    * This useEffect ensures that the run-parameters are always present in the URL, because for some reason the dashboard
    * removes it sometimes if you change the filter or the span (or something else?)
    */
    useGrantParamsOnURL(contextParamsRef, displayParametersRef)

    /*
    * Last useEffect ensures that the run-parameters are always merged and updated considering run, dashboard, default and user
    */
    useEffect(() => {
        if (runPath != undefined && defaultRunparameters != undefined && savedRunparameters != undefined && savedRunparameters.parameters != undefined && userParameters != undefined && dashboardParameters != undefined) {
            const onResetParameters = makeOnResetParameters(savedRunparameters, dashboardParameters, defaultRunparameters)
            const displayParams = makeDisplayParameters(savedRunparameters, dashboardParameters, defaultRunparameters, userParameters)

            setOnResetParameters(onResetParameters)
            setDisplayParameters(displayParams)
        }
    }, [runPath, defaultRunparameters, savedRunparameters, userParameters, dashboardParameters])

    useEffect(() => {
        const event = 'drilling-operations-span-picker-refreshEvent'
        document.addEventListener(event, (e) => {
            fetchRunPath()
        })
        return () => {
            document.removeEventListener(event, fetchRunPath)
        }
    }, [operationsSpanPicker])

    return (authorization.Permission.isAdmin || authorization.Permission.VIEW_RUN_PARAMETERS || authorization.Permission.TRY_RUN_PARAMETERS || authorization.Permission.MANAGE_RUN_PARAMETERS) && assetLookupIsPresent && runPathIsValid ? (
        displayParameters != undefined && displayParameters.length > 0 && onResetParameters != undefined && onResetParameters.length > 0 ? (
            <StyledContainer
                className={'dashboard-filters dropdown-container'}
                dark={context.theme === 'dark'}
                pluginHeaderNavigationDetected={pluginHeaderNavigationDetected}
            >
                <div className={'dropdown btn-group btn-group-default '}>
                    <button
                        className="btn has-icon has-caret btn-default parameters-btn"
                        onClick={() => toggleParameters()}
                    >
                        <span>
                            <i className="fa fa-cog" style={{ paddingLeft: '2px', marginRight: '8px' }} />
                            <span className={'label-bold'}>Run Parameters</span>
                            {diffParametersFlag && (
                                <span className="diff-parameters-flag">
                                    !
                                </span>
                            )}
                            <i className="caret" />
                        </span>
                    </button>
                    {open && (
                        <div className='parameters-list-container'>
                            <ParametersList
                                toggle={toggleParameters}
                                history={history}
                                setUpdateHistory={setUpdateHistory}
                                updateHistory={updateHistory}
                                contextParams={params}
                                runPath={runPath}
                                displayParameters={displayParameters}
                                onResetParameters={onResetParameters}
                                defaultRunparameters={defaultRunparameters}
                                savedRunparameters={savedRunparameters}
                                userParameters={userParameters}
                                dashboardParameters={dashboardParameters}
                                updateByRun={updateByRun}
                                updateByUser={updateByUser}
                                dark={context.theme === 'dark'}
                                pluginHeaderNavigationDetected={pluginHeaderNavigationDetected}
                                span={state?.currentSpan}
                                setDiffParametersFlag={setDiffParametersFlag}
                                deleteRunParametersHistoryItem={deleteRunParametersHistoryItem}
                            />
                        </div>
                    )}
                </div>
            </StyledContainer>
        ) : null
    ) : null
}

const StyledContainer = styled.div`
    ${props =>
        !props.pluginHeaderNavigationDetected &&
        css`
            margin-right: 5px;
        `}

    display: flex;
    padding-left: 8px;

    .btn.has-icon.has-caret.btn-default:focus {
        outline: none;
    }

    .diff-parameters-flag {
        position: absolute;
        top: -3px;
        right: -5px;
        padding: 4px 4px 4px 3px;
        line-height: 4px;
        font-size: 12px;
        font-style: normal;
        border: solid 1px #868695;
        font-weight: bold;
        border-radius: 50%;
        color: #d90000;
        background: #f9e47f;
        font-family: 'SourceSansPro-Regular', Arial, 'Helvetica Neue', Helvetica, sans-serif;
    }

    ${props =>
        props.dark &&
        css`
            background-color: #364159;

            .parameters-btn {
                border-radius: 3px;
                background-image: linear-gradient(180deg, #28334a, #1b273e);
                border: 1px solid #172033;
                color: #fff;
            }
        `}
`
